import { useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import DispatchStatusBadge from '../../../components/DispatchStatusBadge'
import type { DispatchProgress } from '../../../lib/dispatchProgress'
import { fmtDate } from '../types'
import type { DeliveryNote } from '../types'

// Side drawer for ONE Inbound row's notes (Gary, 2026-09-29): staff see the row's context (how
// much is on it, how much has gone out, which notes already exist) BEFORE deciding to raise
// another one -- so no accidental duplicates -- and it slides over the list, so closing it leaves
// them exactly where they were. Opening/creating a note still goes through the Notes page's
// editor (deep link + back link), which returns here when closed.
export interface DrawerRow {
  jobRef: string | null
  houseRef: string | null
  group: string | null
  destination: string | null
  mode: string | null
  plts: number | null
  ctns: number
  kgs: number | null
  cbm: number | null
}

const LIFECYCLE_CLS: Record<DeliveryNote['lifecycle_status'], string> = {
  DRAFT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  PLANNED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
}

export default function RowNotesDrawer({ row, notes, progress, newNoteLink, openNoteLink, userId, onClose, onChanged }: {
  row: DrawerRow
  notes: DeliveryNote[]
  progress: DispatchProgress
  newNoteLink: string | null
  openNoteLink: (n: DeliveryNote) => string | null
  userId: string | undefined
  onClose: () => void
  onChanged: () => Promise<void> | void
}) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pct = progress.total > 0 ? Math.min(100, Math.round(progress.completed / progress.total * 100)) : 0
  const sorted = [...notes].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date) || a.serial_no - b.serial_no)

  async function voidNote(n: DeliveryNote) {
    const reason = window.prompt(`Void note #${n.serial_no}? Its ${n.nop} ctns go back onto this row. Optional reason:`)
    if (reason === null) return
    setBusyId(n.id); setError(null)
    const { error: err } = await supabase.from('delivery_notes').update({
      status: 'VOID', voided_at: new Date().toISOString(), voided_by: userId || null, void_reason: reason || null,
    }).eq('id', n.id)
    setBusyId(null)
    if (err) { setError(err.message); return }
    await onChanged()
  }

  // Same lifecycle write as Notes.tsx pushToPlanned, minus the PDF regeneration (that needs the
  // editor's form state). A row is only on Inbound once its container is devanned + inbounded, so
  // readiness is READY by definition here.
  async function pushNote(n: DeliveryNote) {
    if (!n.fulfilment_type) { setError(`Note #${n.serial_no} has no fulfilment type -- open it and choose one before pushing.`); return }
    setBusyId(n.id); setError(null)
    const now = new Date().toISOString()
    const { error: err } = await supabase.from('delivery_notes').update({
      lifecycle_status: 'PLANNED', planned_at: now, planned_by: userId || null,
      readiness_status: 'READY', readiness_checked_at: now,
      transit_status: n.fulfilment_type === 'COLLECTION_AND_DELIVERY' ? 'PENDING' : null,
    }).eq('id', n.id).eq('lifecycle_status', 'DRAFT')
    setBusyId(null)
    if (err) { setError(err.message); return }
    await onChanged()
  }

  // Undo a push: back to DRAFT, off Planned Outbound (and Deliveries). Guarded to PLANNED only so a
  // COMPLETED (already collected) note can never be reverted from here.
  async function unpushNote(n: DeliveryNote) {
    if (!window.confirm(`Unpush note #${n.serial_no}? It goes back to DRAFT and leaves Planned Outbound.`)) return
    setBusyId(n.id); setError(null)
    const { error: err } = await supabase.from('delivery_notes').update({
      lifecycle_status: 'DRAFT', planned_at: null, planned_by: null,
      readiness_status: null, readiness_checked_at: null, transit_status: null,
    }).eq('id', n.id).eq('lifecycle_status', 'PLANNED')
    setBusyId(null)
    if (err) { setError(err.message); return }
    await onChanged()
  }

  async function deleteDraft(n: DeliveryNote) {
    if (!window.confirm(`Delete draft note #${n.serial_no}? This can't be undone.`)) return
    setBusyId(n.id); setError(null)
    // lifecycle_status guard: only a real DRAFT can ever be deleted from here.
    const { error: err } = await supabase.from('delivery_notes').delete().eq('id', n.id).eq('lifecycle_status', 'DRAFT')
    setBusyId(null)
    if (err) { setError(err.message); return }
    await onChanged()
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-label="Row notes">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="relative w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl flex flex-col h-full">
        <div className="px-5 pt-4 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-400">Notes for this row</p>
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {row.jobRef || '—'}{row.houseRef ? ` · ${row.houseRef}` : ''}{row.group ? ` · ${row.group}` : ''}
            </h3>
            {row.destination && <p className="text-xs text-gray-500 truncate" title={row.destination}>{row.destination}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 space-y-3">
          <div className="grid grid-cols-4 gap-2 text-center">
            {[['Plts', row.plts ?? '—'], ['Ctns', row.ctns], ['Wt (kg)', row.kgs ?? '—'], ['CBM', row.cbm ?? '—']].map(([l, v]) => (
              <div key={l as string} className="bg-gray-50 dark:bg-gray-900/40 rounded-lg py-1.5">
                <div className="text-sm font-bold font-mono text-gray-900 dark:text-white">{v}</div>
                <div className="text-[10px] text-gray-400">{l}</div>
              </div>
            ))}
          </div>
          {row.mode && <p className="text-xs text-gray-500">Mode: <span className="text-gray-700 dark:text-gray-300">{row.mode}</span></p>}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-600 dark:text-gray-300">Dispatched <strong className="font-mono">{progress.completed}</strong> / {progress.total} ctns</span>
              <DispatchStatusBadge progress={progress} />
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div className={`h-full ${progress.state === 'over' ? 'bg-red-500' : progress.completed >= progress.total ? 'bg-green-500' : 'bg-orange-400'}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {error && <p className="text-xs text-red-600">{error}</p>}
          {sorted.length === 0 && <p className="text-sm text-gray-400 text-center py-6">No notes for this row yet.</p>}
          {sorted.map(n => {
            const link = openNoteLink(n)
            const isDraft = n.lifecycle_status === 'DRAFT'
            const isPlanned = n.lifecycle_status === 'PLANNED'
            const busy = busyId === n.id
            return (
              <div key={n.id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 text-sm">
                    <span className="font-mono font-semibold">#{n.serial_no}</span>
                    <span className="text-gray-500"> · {n.release_to_name || '—'} · {fmtDate(n.scheduled_date)} · {n.nop} ctns</span>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${LIFECYCLE_CLS[n.lifecycle_status]}`}>{n.lifecycle_status}</span>
                </div>
                <div className="flex items-center gap-2 mt-1.5 text-xs">
                  {link
                    ? <Link to={link} className="px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium">{isDraft ? 'Edit' : isPlanned ? 'Open' : 'View'}</Link>
                    : <span className="text-gray-400">Not linked to a devanning</span>}
                  {n.pdf_url && <a href={n.pdf_url} target="_blank" rel="noreferrer" className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 font-medium">PDF</a>}
                  {isDraft && <button onClick={() => pushNote(n)} disabled={busy} className="px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 font-medium disabled:opacity-50">Push to Planned</button>}
                  {isPlanned && <button onClick={() => unpushNote(n)} disabled={busy} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 font-medium disabled:opacity-50">Unpush</button>}
                  {isDraft
                    ? <button onClick={() => deleteDraft(n)} disabled={busy} className="px-2 py-1 text-red-500 hover:underline disabled:opacity-50">Delete</button>
                    : <button onClick={() => voidNote(n)} disabled={busy} className="px-2 py-1 text-red-500 hover:underline disabled:opacity-50">Void</button>}
                </div>
              </div>
            )
          })}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          {newNoteLink
            ? <Link to={newNoteLink} className="block w-full text-center px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700">+ New Note</Link>
            : <p className="text-xs text-gray-400 text-center">This row isn't linked to a devanning, so notes can't be created here.</p>}
          {progress.state === 'done' && <p className="text-[11px] text-gray-400 text-center mt-1.5">{progress.completed >= progress.total ? 'Everything on this row has left the warehouse.' : 'Notes already cover this whole row -- confirm release in Planned Outbound.'}</p>}
        </div>
      </aside>
    </div>
  )
}
